import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Cpu, Server, CheckCircle2, XCircle, Loader2, RefreshCw,
  Activity, Clock, Package, GitBranch, AlertTriangle,
  Wand2, Copy, Terminal, Info,
} from 'lucide-react';

const API = `${import.meta.env.VITE_API_BASE || ''}/api`;

const MODE_INFO = {
  embedded: {
    label:  'Embedded',
    color:  'text-slate-300',
    bg:     'bg-slate-800 border-slate-700',
    hint:   'Workers run inside the backend container. Simplest — fine for dev, preview, and low-traffic prod.',
  },
  service: {
    label:  'Service',
    color:  'text-emerald-300',
    bg:     'bg-emerald-500/10 border-emerald-500/40',
    hint:   'Workers run in a separate ECS Service / EKS Deployment. Recommended for production.',
  },
  on_demand: {
    label:  'On-demand',
    color:  'text-cyan-300',
    bg:     'bg-cyan-500/10 border-cyan-500/40',
    hint:   'Backend spawns an ECS task per job (future).',
  },
  hybrid: {
    label:  'Hybrid',
    color:  'text-purple-300',
    bg:     'bg-purple-500/10 border-purple-500/40',
    hint:   'One warm runner + auto-scale burst (future).',
  },
};

export default function RunnerFleet() {
  const [data, setData] = useState(null);
  const [engines, setEngines] = useState(null);
  const [config, setConfig] = useState(null);        // /runtime-config/runner
  const [deployStatus, setDeployStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [savingMode, setSavingMode] = useState(false);
  const [showWizard, setShowWizard] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [fleetRes, engineRes, cfgRes, statusRes] = await Promise.all([
        axios.get(`${API}/orchestration/workspaces/runner/fleet`, { withCredentials: true }),
        axios.get(`${API}/orchestration/workspaces/runner/engines`, { withCredentials: true }),
        axios.get(`${API}/runtime-config/runner`, { withCredentials: true }),
        axios.get(`${API}/runtime-config/runner/deploy-status`, { withCredentials: true }),
      ]);
      setData(fleetRes.data);
      setEngines(engineRes.data);
      setConfig(cfgRes.data);
      setDeployStatus(statusRes.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); setRefreshing(false); }
  };

  const changeMode = async (newMode, workerCount = null) => {
    setSavingMode(true);
    try {
      await axios.post(`${API}/runtime-config/runner`,
        { mode: newMode, worker_count: workerCount, reason: 'Changed from Runner Fleet UI' },
        { withCredentials: true });
      // Give the supervisor ~6s to reconcile before we refresh.
      setTimeout(() => load(), 6000);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSavingMode(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);   // auto-refresh every 15s
    return () => clearInterval(t);
  }, []);

  const modeKey = data?.runner_mode || 'embedded';
  const mode = MODE_INFO[modeKey] || MODE_INFO.embedded;

  // "Double-running" detection — if backend is in embedded mode but there
  // are workers heartbeating from standalone runner containers (or vice
  // versa), operators end up with racing consumers. This is the single
  // most common CG_RUNNER_MODE misconfiguration; call it out loudly.
  const mismatchWorkers = (data?.workers || []).filter((w) => {
    const wm = (w.runner_mode || '').toLowerCase();
    return wm && wm !== modeKey;
  });
  const hasModeMismatch = mismatchWorkers.length > 0;

  return (
    <div className="min-h-screen text-slate-100 pb-24" data-testid="runner-fleet-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-8">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
                <Cpu className="w-5 h-5 text-purple-300" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight" data-testid="runner-fleet-heading">
                Runner Fleet
              </h1>
            </div>
            <p className="text-slate-400 text-sm max-w-2xl">
              Live view of every runner worker across the cluster, its executor mode,
              and the current job queue depth. Every worker heartbeats every&nbsp;
              <b>{data?.heartbeat_seconds || 15}s</b>; anything older than&nbsp;
              <b>{data?.stale_after_seconds || 45}s</b> is marked unhealthy.
            </p>
          </div>
          <button
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-700 hover:border-purple-500/50 text-slate-200 text-sm disabled:opacity-50"
            data-testid="runner-fleet-refresh-btn"
          >
            {refreshing
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Refreshing…</>
              : <><RefreshCw className="w-4 h-4" /> Refresh</>}
          </button>
        </div>

        {/* Mode banner — now with a LIVE dropdown, no redeploy required */}
        <div className={`mt-6 p-5 rounded-xl border ${mode.bg}`} data-testid="runner-fleet-mode-banner">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span className="text-xs uppercase tracking-wide text-slate-500">Runner mode</span>
            <select
              value={config?.mode || modeKey}
              onChange={(e) => changeMode(e.target.value)}
              disabled={savingMode || !config}
              data-testid="runner-mode-dropdown"
              className="px-2 py-1 rounded bg-slate-950 border border-slate-700 text-sm font-mono focus:outline-none focus:border-purple-500/60 disabled:opacity-50"
            >
              {(config?.valid_modes || ['embedded', 'service', 'hybrid']).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            {config?.mode === 'embedded' && (
              <>
                <span className="text-xs text-slate-500">workers</span>
                <input type="number" min={1} max={32}
                  value={config?.worker_count || 2}
                  onChange={(e) => changeMode(config.mode, Math.max(1, Math.min(32, Number(e.target.value))))}
                  disabled={savingMode}
                  data-testid="runner-worker-count-input"
                  className="w-16 px-2 py-1 rounded bg-slate-950 border border-slate-700 text-sm font-mono focus:outline-none focus:border-purple-500/60 disabled:opacity-50" />
              </>
            )}
            {savingMode && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            <button onClick={() => setShowWizard(true)}
              data-testid="runner-deploy-wizard-btn"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-purple-500/50 text-purple-200 hover:bg-purple-500/10 text-xs">
              <Wand2 className="w-3.5 h-3.5" />
              Deploy standalone runners
            </button>
          </div>
          <div className="text-sm text-slate-300">{mode.hint}</div>
          {config?.updated_by && (
            <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-1.5" data-testid="runner-mode-audit">
              <Info className="w-3 h-3" />
              Last changed by <code className="text-slate-300">{config.updated_by}</code>
              {' '}at <code className="text-slate-300">{(config.updated_at || '').slice(0, 19).replace('T', ' ')}</code>.
              Supervisor reconciles workers within ~5s — no redeploy needed.
              {' '}
              <a href="https://github.com/cloudgenie/cloudgenie/blob/main/docs/guides/RUNNER_MODE_CONFIG.md"
                target="_blank" rel="noopener noreferrer"
                className="text-purple-300 hover:text-purple-200 underline">
                Env-var bootstrap docs
              </a>
            </div>
          )}
        </div>

        {hasModeMismatch && (
          <div className="mt-4 p-4 rounded-xl border border-rose-500/50 bg-rose-500/10"
                data-testid="runner-fleet-mismatch-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-300 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-rose-200">
                  Runner mode mismatch — you&apos;re double-running workers
                </div>
                <div className="text-xs text-rose-300/90 mt-1">
                  This backend advertises <code>{modeKey}</code> mode, but{' '}
                  <b>{mismatchWorkers.length}</b> worker(s) are heartbeating from{' '}
                  <code>{[...new Set(mismatchWorkers.map((w) => w.runner_mode))].join(', ')}</code>{' '}
                  containers. Both sets race for the same Mongo jobs, which will
                  cause duplicate applies and unpredictable failures.
                </div>
                <div className="text-xs text-rose-200/90 mt-2">
                  <b>Fix:</b> pick one side to shut down. Either set{' '}
                  <code>CG_RUNNER_MODE=service</code> on this backend and redeploy
                  (so embedded workers stop), or stop the standalone runner
                  containers. See{' '}
                  <code>docs/guides/RUNNER_MODE_CONFIG.md</code> for the exact
                  place to set this env var for your platform (ECS, Kubernetes,
                  Docker Compose, systemd, Vercel/Render/etc.).
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Totals */}
        {loading ? (
          <div className="mt-8 flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading fleet…
          </div>
        ) : error ? (
          <div className="mt-8 p-3 rounded-lg border border-rose-500/40 bg-rose-500/5 text-rose-300 text-sm">{error}</div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="runner-fleet-totals">
              <Kpi title="Workers"       value={data.totals.workers}      Icon={Server}          color="text-purple-300" />
              <Kpi title="Healthy"        value={data.totals.healthy}      Icon={CheckCircle2}    color="text-emerald-300" />
              <Kpi title="Queued jobs"    value={data.totals.queued_jobs}  Icon={Clock}           color="text-amber-300" />
              <Kpi title="Running now"    value={data.totals.running_jobs} Icon={Activity}        color="text-cyan-300" />
            </div>

            <div className="mt-6 grid md:grid-cols-3 gap-3" data-testid="runner-fleet-history">
              <Kpi title="Done (lifetime)"   value={data.totals.done_jobs}   Icon={CheckCircle2} color="text-emerald-300" />
              <Kpi title="Failed (lifetime)" value={data.totals.failed_jobs} Icon={XCircle}      color="text-rose-300" />
              <Kpi title="Unhealthy" value={data.totals.unhealthy}
                    Icon={data.totals.unhealthy > 0 ? AlertTriangle : CheckCircle2}
                    color={data.totals.unhealthy > 0 ? 'text-rose-300' : 'text-emerald-300'} />
            </div>

            {/* Engines */}
            {engines && (
              <div className="mt-8">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Installed engines · {engines.runner_mode}</div>
                <div className="flex flex-wrap gap-2 text-xs" data-testid="runner-fleet-engines">
                  {engines.engines.map((e) => (
                    <span key={e.id} className={`px-2 py-1 rounded-lg border font-mono ${
                      e.installed
                        ? 'bg-slate-900 border-slate-700 text-slate-300'
                        : 'bg-slate-900/50 border-slate-800 text-slate-500 line-through'
                    }`}>
                      {e.id}{e.installed && e.version ? ` · ${e.version.slice(0, 30)}` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Worker table */}
            <div className="mt-8">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Workers</div>
              {data.workers.length === 0 ? (
                <div className="p-8 rounded-xl border border-dashed border-slate-800 bg-slate-900/30 text-center"
                      data-testid="runner-fleet-empty">
                  <Server className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                  <div className="text-slate-200 font-semibold mb-1">No workers heartbeating right now</div>
                  <div className="text-slate-500 text-sm max-w-md mx-auto">
                    In <b>service</b> mode this usually means your runner ECS/EKS deployment
                    is not running. In <b>embedded</b> mode set
                    <code className="text-slate-300"> CG_RUNNER_ENABLED=true</code>.
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden"
                      data-testid="runner-fleet-table">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-950/60">
                      <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                        <th className="px-4 py-2">Worker</th>
                        <th className="px-4 py-2">Pod</th>
                        <th className="px-4 py-2">Executor</th>
                        <th className="px-4 py-2">Mode</th>
                        <th className="px-4 py-2">Last heartbeat</th>
                        <th className="px-4 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.workers.map((w) => (
                        <tr key={`${w.pod_id}/${w.worker_id}`}
                             className="border-t border-slate-800/60 hover:bg-slate-900/60"
                             data-testid={`worker-row-${w.worker_id}`}>
                          <td className="px-4 py-2 font-mono text-slate-200">{w.worker_id}</td>
                          <td className="px-4 py-2 text-xs text-slate-400 truncate max-w-[220px]">{w.pod_id}</td>
                          <td className="px-4 py-2 text-xs text-slate-400">{w.executor}</td>
                          <td className="px-4 py-2 text-xs">
                            <span className={`px-2 py-0.5 rounded-full font-mono border text-xs ${
                              MODE_INFO[w.mode]?.bg || 'bg-slate-800 border-slate-700'
                            } ${MODE_INFO[w.mode]?.color || 'text-slate-300'}`}>
                              {MODE_INFO[w.mode]?.label || w.mode}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-400">
                            {w.age_seconds != null ? `${w.age_seconds}s ago` : '—'}
                          </td>
                          <td className="px-4 py-2 text-xs">
                            {w.healthy ? (
                              <span className="inline-flex items-center gap-1 text-emerald-300">
                                <CheckCircle2 className="w-3 h-3" /> Healthy
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-rose-300">
                                <XCircle className="w-3 h-3" /> Unhealthy
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Deploy hint for service mode — now points at the live wizard */}
            {modeKey === 'embedded' && (
              <div className="mt-8 p-5 rounded-xl border border-purple-500/30 bg-purple-500/5"
                    data-testid="runner-fleet-service-cta">
                <div className="flex items-start gap-3">
                  <GitBranch className="w-5 h-5 text-purple-300 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-purple-100">
                      Ready for production? Switch to <b>service</b> mode.
                    </div>
                    <div className="text-sm text-slate-400 mt-1 max-w-2xl">
                      Click <b>Deploy standalone runners</b> at the top of this page —
                      the wizard uses credentials from Sandbox Vault to generate a
                      copy-pasteable Terraform command for AWS ECS or GCP Cloud Run.
                      Once the standalone containers are running, flip the mode
                      dropdown to <code>service</code> and this backend hands off
                      cleanly (embedded workers drain, standalone workers take over).
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {showWizard && (
        <DeployWizardModal
          onClose={() => setShowWizard(false)}
          deployStatus={deployStatus}
          onDeployed={() => { setShowWizard(false); load(); }}
        />
      )}
    </div>
  );
}

// ── Deploy wizard modal — Phase B ────────────────────────────────────

function DeployWizardModal({ onClose, deployStatus, onDeployed }) {
  const [cloud, setCloud] = useState('aws');
  const [variant, setVariant] = useState('baseline');
  const [region, setRegion] = useState('us-east-1');
  const [clusterArn, setClusterArn] = useState('');
  const [imageUri, setImageUri] = useState('');
  const [projectId, setProjectId] = useState('');
  const [credId, setCredId] = useState('');
  const [command, setCommand] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState(null);

  const availableCreds = (deployStatus?.available_cloud_creds || [])
    .filter((c) => c.provider === cloud);

  const generate = async () => {
    setGenerating(true); setErr(null); setCommand(null);
    try {
      const body = { cloud, variant, region: region || undefined };
      if (cloud === 'aws') {
        body.cluster_arn = clusterArn || undefined;
        body.image_uri   = imageUri || undefined;
      } else {
        body.project_id = projectId || undefined;
        body.image_uri  = imageUri || undefined;
      }
      if (credId) body.cred_id = credId;
      const { data } = await axios.post(`${API}/runtime-config/runner/deploy-command`,
        body, { withCredentials: true });
      setCommand(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setGenerating(false); }
  };

  const copy = async () => {
    if (!command?.command) return;
    try {
      await navigator.clipboard.writeText(command.command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — user can select the text */ }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}
         data-testid="runner-deploy-wizard-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-300" />
            <h2 className="text-lg font-semibold text-slate-100">Deploy standalone runners</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200" data-testid="wizard-close-btn">×</button>
        </div>

        {/* Landing summary */}
        {deployStatus && (
          <div className="mb-4 p-3 rounded-lg border border-slate-800 bg-slate-950 text-xs text-slate-300">
            <div className="font-semibold text-slate-200 mb-1">Current state</div>
            {deployStatus.message}
          </div>
        )}

        {/* Cloud picker */}
        <div className="mb-3">
          <div className="text-xs uppercase text-slate-500 mb-1">Cloud</div>
          <div className="grid grid-cols-2 gap-2">
            {[['aws', 'AWS ECS Fargate'], ['gcp', 'GCP Cloud Run']].map(([id, label]) => (
              <button key={id} onClick={() => { setCloud(id); setCommand(null); }}
                data-testid={`wizard-cloud-${id}`}
                className={`p-3 rounded-lg border text-sm ${cloud === id ? 'border-purple-500/60 bg-purple-500/10 text-purple-200' : 'border-slate-800 text-slate-400 hover:border-slate-700'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {cloud === 'aws' && (
          <div className="mb-3">
            <div className="text-xs uppercase text-slate-500 mb-1">Variant</div>
            <div className="grid grid-cols-2 gap-2">
              {[['baseline', 'Baseline (static count)'], ['hybrid', 'Hybrid (autoscaling)']].map(([id, label]) => (
                <button key={id} onClick={() => { setVariant(id); setCommand(null); }}
                  data-testid={`wizard-variant-${id}`}
                  className={`p-2 rounded border text-xs ${variant === id ? 'border-purple-500/60 bg-purple-500/10 text-purple-200' : 'border-slate-800 text-slate-400 hover:border-slate-700'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Fields */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="block col-span-1">
            <div className="text-xs text-slate-400 mb-1">Region</div>
            <input value={region} onChange={(e) => setRegion(e.target.value)}
              placeholder={cloud === 'aws' ? 'us-east-1' : 'us-central1'}
              data-testid="wizard-region"
              className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm" />
          </label>
          {cloud === 'aws' ? (
            <>
              <label className="block col-span-1">
                <div className="text-xs text-slate-400 mb-1">ECS Cluster ARN</div>
                <input value={clusterArn} onChange={(e) => setClusterArn(e.target.value)}
                  placeholder="arn:aws:ecs:…:cluster/prod"
                  data-testid="wizard-cluster-arn"
                  className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm" />
              </label>
              <label className="block col-span-2">
                <div className="text-xs text-slate-400 mb-1">Runner image URI</div>
                <input value={imageUri} onChange={(e) => setImageUri(e.target.value)}
                  placeholder="1234.dkr.ecr.us-east-1.amazonaws.com/cloudgenie-runner:v1"
                  data-testid="wizard-image-uri"
                  className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm font-mono" />
              </label>
            </>
          ) : (
            <>
              <label className="block col-span-1">
                <div className="text-xs text-slate-400 mb-1">GCP Project ID</div>
                <input value={projectId} onChange={(e) => setProjectId(e.target.value)}
                  placeholder="my-gcp-project"
                  data-testid="wizard-project-id"
                  className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm" />
              </label>
              <label className="block col-span-2">
                <div className="text-xs text-slate-400 mb-1">Runner image URI</div>
                <input value={imageUri} onChange={(e) => setImageUri(e.target.value)}
                  placeholder="us-central1-docker.pkg.dev/my-proj/cg/runner:v1"
                  data-testid="wizard-image-uri-gcp"
                  className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm font-mono" />
              </label>
            </>
          )}
        </div>

        {availableCreds.length > 0 && (
          <label className="block mb-3">
            <div className="text-xs text-slate-400 mb-1">
              Sandbox Vault credential (optional — used to auto-fill region / project)
            </div>
            <select value={credId} onChange={(e) => setCredId(e.target.value)}
              data-testid="wizard-cred-select"
              className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm">
              <option value="">— none —</option>
              {availableCreds.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.provider})</option>
              ))}
            </select>
          </label>
        )}

        <div className="flex justify-end gap-2 mb-3">
          <button onClick={generate} disabled={generating}
            data-testid="wizard-generate-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm disabled:opacity-50">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Terminal className="w-4 h-4" />}
            Generate command
          </button>
        </div>

        {err && (
          <div className="p-3 rounded border border-rose-500/40 bg-rose-500/10 text-rose-300 text-xs mb-3">
            {err}
          </div>
        )}

        {command && (
          <div data-testid="wizard-command-output">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs uppercase text-slate-500">Copy-paste into your terminal</div>
              <button onClick={copy}
                data-testid="wizard-copy-btn"
                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-700 text-xs text-slate-300 hover:border-purple-500/60">
                <Copy className="w-3 h-3" /> {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <pre className="bg-slate-950 border border-slate-800 rounded p-3 text-xs font-mono text-emerald-300 whitespace-pre-wrap overflow-x-auto">
{command.command}
            </pre>
            {command.notes && command.notes.length > 0 && (
              <div className="mt-3 p-3 rounded-lg border border-slate-800 bg-slate-950 text-xs text-slate-400">
                <div className="font-semibold text-slate-200 mb-1">Before you run this</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  {command.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </div>
            )}
            <div className="mt-3 p-3 rounded-lg border border-purple-500/30 bg-purple-500/5 text-xs text-purple-200">
              <div className="font-semibold mb-1">Next steps</div>
              <ol className="list-decimal pl-5 space-y-0.5">
                <li>Run the command above from a terminal that has your cloud credentials exported.</li>
                <li>Wait ~90 seconds for the ECS task / Cloud Run instance to become healthy.</li>
                <li>Come back to this page — new heartbeats should appear in the workers table.</li>
                <li>Flip the mode dropdown at the top of this page to <code>service</code>. Embedded workers drain automatically — no restart.</li>
              </ol>
            </div>
            <div className="mt-3 text-right">
              <button onClick={onDeployed}
                data-testid="wizard-done-btn"
                className="px-4 py-2 rounded-lg border border-slate-700 hover:border-purple-500/60 text-slate-200 text-sm">
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ title, value, Icon, color }) {
  return (
    <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/40"
          data-testid={`runner-fleet-kpi-${title.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${color}`} />
        <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}
